/* SSi website – dedicated Testimonials page blocks.
 *
 * Reuses the CELEBS + LEARNERS data from Testimonials.jsx. Adds:
 *   – TPHero                hero with stat row
 *   – TPWrittenWall         three pull-quote cards from celebs
 *   – TPFilteredGrid        filter-by-language grid of videos (celebs + learners merged)
 *   – TPShareYourStory      invitation card at the foot of the page
 *
 * The language filter is deliberately simple: All / Welsh / Spanish.
 * It inspects each entry's `role` string for "Welsh" or "Spanish".
 */
import { Fragment, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CELEBS, LEARNERS } from './Testimonials.jsx';

export function TPHero({ t }) {
  return (
    <section className="ssi-tp__hero" style={tpHeroStyles.wrap}>
      <div style={tpHeroStyles.inner}>
        <div style={tpHeroStyles.kicker}>{t.tpKicker}</div>
        <h1 style={tpHeroStyles.h1}>{(t.tpTitle || '').split('\n').map((line, i, arr) => (
          <Fragment key={i}>{line}{i < arr.length - 1 && <br/>}</Fragment>
        ))}</h1>
        <p style={tpHeroStyles.lede}>{t.tpLede}</p>
        <div className="ssi-tp__stats" style={tpHeroStyles.stats}>
          <div style={tpHeroStyles.stat}>
            <div style={tpHeroStyles.statNum}>{t.tpStat1Num}</div>
            <div style={tpHeroStyles.statLabel}>{t.tpStat1Label}</div>
          </div>
          <div style={tpHeroStyles.statSep}/>
          <div style={tpHeroStyles.stat}>
            <div style={tpHeroStyles.statNum}>{t.tpStat2Num}</div>
            <div style={tpHeroStyles.statLabel}>{t.tpStat2Label}</div>
          </div>
          <div style={tpHeroStyles.statSep}/>
          <div style={tpHeroStyles.stat}>
            <div style={tpHeroStyles.statNum}>{t.tpStat3Num}</div>
            <div style={tpHeroStyles.statLabel}>{t.tpStat3Label}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TPWrittenWall({ t }) {
  const quotes = [
    { q: t.tpWallQ1, n: t.tpWallN1, r: t.tpWallR1, photo: 'celeb-scott-quinnell.jpg' },
    { q: t.tpWallQ2, n: t.tpWallN2, r: t.tpWallR2, photo: 'celeb-carol-vorderman.jpg' },
    { q: t.tpWallQ3, n: t.tpWallN3, r: t.tpWallR3, photo: 'celeb-rakie-ayola.jpg' },
    { q: t.tpWallQ4, n: t.tpWallN4, r: t.tpWallR4, photo: 'leanne-wood.jpg' },
    { q: t.tpWallQ5, n: t.tpWallN5, r: t.tpWallR5, photo: 'rachel-healy.png' },
    { q: t.tpWallQ6, n: t.tpWallN6, r: t.tpWallR6, photo: 'tess-fitzpatrick.png' },
  ].filter(o => o.q && o.n);
  const initials = (n) => n.split(/\s+/).filter(Boolean).slice(0,2).map(w=>w[0].toUpperCase()).join('');
  return (
    <section className="ssi-tp__wall" style={tpWallStyles.wrap}>
      <div style={tpWallStyles.head}>
        <div style={tpWallStyles.kicker}>{t.tpWallKicker}</div>
        <h2 style={tpWallStyles.h2}>{t.tpWallTitle}</h2>
      </div>
      <div className="ssi-tp__wallGrid" style={tpWallStyles.grid}>
        {quotes.map((qObj, i) => (
          <figure key={i} style={tpWallStyles.card}>
            <svg width="38" height="30" viewBox="0 0 38 30" style={tpWallStyles.mark} aria-hidden="true">
              <path d="M0 30V17.2C0 9.1 2.3 3.3 10.5 0l3.3 3.6c-5 2.6-7 6-7 10.6h7V30H0zm21 0V17.2C21 9.1 23.3 3.3 31.5 0l3.3 3.6c-5 2.6-7 6-7 10.6H35V30H21z" fill="#C2130C"/>
            </svg>
            <blockquote style={tpWallStyles.quote}>{qObj.q}</blockquote>
            <figcaption style={tpWallStyles.cap}>
              {qObj.photo
                ? <img src={`/assets/${qObj.photo}`} alt="" style={tpWallStyles.photo}/>
                : <div style={tpWallStyles.monogram} aria-hidden="true">{initials(qObj.n)}</div>
              }
              <div>
                <div style={tpWallStyles.name}>{qObj.n}</div>
                <div style={tpWallStyles.role}>{qObj.r}</div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

export function TPFilteredGrid({ t }) {
  const [lang, setLang] = useState('all');
  const [openVid, setOpenVid] = useState(null);

  const celebs   = CELEBS;
  const learners = LEARNERS;

  const matchesLang = (role) => {
    if (lang === 'all') return true;
    const r = (role || '').toLowerCase();
    return r.includes(lang);
  };

  const celebsF   = celebs.filter(c => matchesLang(c.role) || celebs.length && lang==='all');
  const learnersF = learners.filter(l => matchesLang(l.role));
  // For celebrities we don't have per-language labels (they're all Welsh learners);
  // show them only when "all" or "welsh" is selected.
  const showCelebs = (lang === 'all' || lang === 'welsh');

  useEffect(() => {
    if (!openVid) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpenVid(null); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [openVid]);

  return (
    <section className="ssi-tp__grid" style={tpGridStyles.wrap} id="stories">
      <div style={tpGridStyles.head}>
        <div style={tpGridStyles.kicker}>{t.tpGridKicker}</div>
        <h2 style={tpGridStyles.h2}>{t.tpGridTitle}</h2>
        <p style={tpGridStyles.sub}>{t.tpGridSub}</p>
      </div>

      {showCelebs && (
        <>
          <div style={tpGridStyles.sectionHead}>
            <span style={tpGridStyles.sectionLabel}>{t.tpGroupCelebs}</span>
            <span style={tpGridStyles.sectionCount}>{celebsF.length} videos</span>
          </div>
          <div className="ssi-tp__rowCelebs" style={tpGridStyles.rowCelebs}>
            {celebsF.map(c => (
              <button key={c.slug} onClick={()=>setOpenVid(c)} style={tpGridStyles.tile} title={`${c.name}`}>
                <div style={tpGridStyles.tileImgWrap}>
                  <img src={`/assets/celeb-${c.slug}.jpg`} alt={c.name} style={tpGridStyles.tileImg}/>
                  <span style={tpGridStyles.tilePlay}>
                    <svg width="14" height="14" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                      <path d="M10 7v14l11-7-11-7z" fill="#fff"/>
                    </svg>
                  </span>
                </div>
                <div style={tpGridStyles.tileName}>{c.name}</div>
                <div style={tpGridStyles.tileRole}>{c.role}</div>
              </button>
            ))}
          </div>
        </>
      )}

      <div style={{...tpGridStyles.sectionHead, marginTop: showCelebs ? 48 : 0}}>
        <span style={tpGridStyles.sectionLabel}>{t.tpGroupLearners}</span>
        <span style={tpGridStyles.sectionCount}>{learnersF.length} videos</span>
      </div>
      <div className="ssi-tp__rowLearners" style={tpGridStyles.rowLearners}>
        {learnersF.map(l => (
          <button key={l.slug} onClick={()=>setOpenVid({ ...l, kind:'learner' })} style={tpGridStyles.tile} title={l.name}>
            <div style={tpGridStyles.tileImgWrap}>
              <img src={`/assets/learner-${l.slug}.jpg`} alt={l.name} style={tpGridStyles.tileImg}/>
              <span style={tpGridStyles.tilePlay}>
                <svg width="14" height="14" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                  <path d="M10 7v14l11-7-11-7z" fill="#fff"/>
                </svg>
              </span>
            </div>
            <div style={tpGridStyles.tileName}>{l.name}</div>
            <div style={tpGridStyles.tileRole}>{l.role}</div>
          </button>
        ))}
        {learnersF.length === 0 && (
          <div style={tpGridStyles.empty}>{t.tpEmpty}</div>
        )}
      </div>

      {openVid && (
        <div style={tpGridStyles.backdrop} onClick={()=>setOpenVid(null)}>
          <div style={tpGridStyles.dialog} onClick={e=>e.stopPropagation()}>
            <button onClick={()=>setOpenVid(null)} style={tpGridStyles.close} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 5l10 10M15 5L5 15" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <video
              src={openVid.src}
              poster={`/assets/${openVid.kind==='learner'?'learner':'celeb'}-${openVid.slug}.jpg`}
              controls autoPlay playsInline
              style={tpGridStyles.video}
            />
            <div style={tpGridStyles.caption}>
              <div style={tpGridStyles.capName}>{openVid.name}</div>
              <div style={tpGridStyles.capRole}>{openVid.role}</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function TPShareYourStory({ t }) {
  return (
    <section className="ssi-tp__share" style={tpShareStyles.wrap}>
      <div className="ssi-tp__shareCard" style={tpShareStyles.card}>
        <div className="ssi-tp__shareCopy" style={tpShareStyles.copy}>
          <div style={tpShareStyles.kicker}>{t.tpShareKicker}</div>
          <h2 style={tpShareStyles.h2}>{t.tpShareTitle}</h2>
          <p style={tpShareStyles.body}>{t.tpShareBody}</p>
          <div style={tpShareStyles.ctaRow}>
            <Link to="/contact" className="btn btn--primary">{t.tpShareCta1}</Link>
          </div>
        </div>
        <div className="ssi-tp__shareSide" style={tpShareStyles.side}>
          <div style={tpShareStyles.sideItem}>
            <div style={tpShareStyles.sideNum}>01</div>
            <div>
              <div style={tpShareStyles.sideHead}>{t.tpShareStep1Title}</div>
              <div style={tpShareStyles.sideBody}>{t.tpShareStep1Body}</div>
            </div>
          </div>
          <div style={tpShareStyles.sideItem}>
            <div style={tpShareStyles.sideNum}>02</div>
            <div>
              <div style={tpShareStyles.sideHead}>{t.tpShareStep2Title}</div>
              <div style={tpShareStyles.sideBody}>{t.tpShareStep2Body}</div>
            </div>
          </div>
          <div style={tpShareStyles.sideItem}>
            <div style={tpShareStyles.sideNum}>03</div>
            <div>
              <div style={tpShareStyles.sideHead}>{t.tpShareStep3Title}</div>
              <div style={tpShareStyles.sideBody}>{t.tpShareStep3Body}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --- styles ---------------------------------------------------- */

const tpHeroStyles = {
  wrap: { background:'var(--offWhite, #FDF5F5)', borderBottom:'1px solid var(--border)', padding:'80px 32px 64px' },
  inner: { maxWidth:1040, margin:'0 auto', textAlign:'center' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  h1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:62, lineHeight:1.05, margin:'0 0 20px', letterSpacing:'-.02em', textWrap:'balance' },
  lede: { fontSize:19, lineHeight:1.55, color:'var(--grey)', margin:'0 auto 36px', maxWidth:'62ch', textWrap:'pretty' },
  stats: { display:'inline-flex', alignItems:'center', gap:36, padding:'18px 32px', background:'#fff', borderRadius:18, border:'1px solid var(--border)' },
  stat: { textAlign:'center', minWidth:120 },
  statNum: { fontFamily:'var(--font-display)', fontSize:36, color:'var(--brandRed)', lineHeight:1, letterSpacing:'-.01em' },
  statLabel: { fontSize:12, color:'var(--grey)', marginTop:6, letterSpacing:'.04em' },
  statSep: { width:1, height:42, background:'var(--border)' },
};

const tpWallStyles = {
  wrap: { padding:'72px 32px 48px', maxWidth:1240, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:680, margin:'0 auto 36px' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.15, margin:0, letterSpacing:'-.01em' },
  grid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:20 },
  card: { background:'#fff', border:'1px solid var(--border)', borderRadius:18, padding:'32px 28px 26px', margin:0, display:'flex', flexDirection:'column', gap:18, minHeight:280 },
  mark: { opacity:.2 },
  quote: { fontFamily:'var(--font-display)', fontSize:19, lineHeight:1.4, color:'var(--fg-1)', margin:0, flex:1, textWrap:'pretty', letterSpacing:'-.005em' },
  cap: { display:'flex', alignItems:'center', gap:12, paddingTop:18, borderTop:'1px solid var(--border)', margin:0 },
  photo: { width:44, height:44, borderRadius:'50%', objectFit:'cover', objectPosition:'center 28%' },
  monogram: { width:44, height:44, borderRadius:'50%', background:'var(--offWhite, #FDF5F5)', border:'1px solid var(--border)', color:'var(--brandRed)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontSize:15, letterSpacing:'.02em', flexShrink:0 },
  name: { fontSize:14.5, fontWeight:600, color:'var(--fg-1)' },
  role: { fontSize:12.5, color:'var(--grey)', marginTop:1 },
};

const tpGridStyles = {
  wrap: { padding:'48px 32px 96px', maxWidth:1440, margin:'0 auto', position:'relative' },
  head: { textAlign:'center', maxWidth:760, margin:'0 auto 36px' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.15, margin:'0 0 12px', letterSpacing:'-.01em' },
  sub: { fontSize:15, color:'var(--grey)', margin:'0 auto', maxWidth:'62ch', textAlign:'center', textWrap:'balance' },
  filters: { display:'inline-flex', gap:8, padding:4, background:'#fff', borderRadius:999, border:'1px solid var(--border)' },
  pill: { padding:'8px 18px', borderRadius:999, border:'1px solid transparent', fontSize:13.5, fontWeight:500, fontFamily:'inherit', cursor:'pointer', transition:'all 140ms' },
  sectionHead: { display:'flex', alignItems:'baseline', justifyContent:'space-between', borderTop:'1px solid var(--border)', paddingTop:20, marginBottom:18 },
  sectionLabel: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700 },
  sectionCount: { fontSize:12, color:'var(--grey)', fontVariantNumeric:'tabular-nums' },
  rowCelebs:   { display:'grid', gridTemplateColumns:'repeat(9, 1fr)', gap:14, marginBottom:8 },
  rowLearners: { display:'grid', gridTemplateColumns:'repeat(11, 1fr)', gap:12 },
  tile: { textDecoration:'none', color:'inherit', display:'flex', flexDirection:'column', textAlign:'left', background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit' },
  tileImgWrap: { position:'relative', aspectRatio:'1/1', borderRadius:14, overflow:'hidden', background:'var(--offWhite)', marginBottom:10 },
  tileImg: { width:'100%', height:'100%', objectFit:'cover', objectPosition:'center 28%', display:'block' },
  tilePlay: { position:'absolute', right:8, bottom:8, width:26, height:26, borderRadius:'50%', background:'rgba(0,0,0,.62)', display:'flex', alignItems:'center', justifyContent:'center', paddingLeft:2 },
  tileName: { fontSize:13, fontWeight:500, color:'var(--fg-1)', lineHeight:1.2 },
  tileRole: { fontSize:11, color:'var(--grey)', marginTop:2, lineHeight:1.3 },
  empty: { gridColumn:'1 / -1', textAlign:'center', padding:'40px 0', color:'var(--grey)', fontSize:14 },
  backdrop: { position:'fixed', inset:0, background:'rgba(20,0,0,.82)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'32px' },
  dialog: { position:'relative', width:'min(1040px, 100%)', maxHeight:'90vh', display:'flex', flexDirection:'column' },
  close: { position:'absolute', top:-44, right:0, width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'1px solid rgba(255,255,255,.3)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', padding:0 },
  video: { width:'100%', maxHeight:'80vh', borderRadius:14, background:'#000', display:'block' },
  caption: { marginTop:16, color:'#fff', textAlign:'center' },
  capName: { fontFamily:'var(--font-display)', fontSize:20 },
  capRole: { fontSize:13, color:'rgba(255,255,255,.7)', marginTop:2 },
};

const tpShareStyles = {
  wrap: { padding:'72px 32px 96px', maxWidth:1240, margin:'0 auto' },
  card: { background:'#fff', border:'1px solid var(--border)', borderRadius:22, overflow:'hidden', display:'grid', gridTemplateColumns:'1.15fr 1fr', alignItems:'stretch', boxShadow:'var(--shadow-sm)' },
  copy: { padding:'56px 56px 56px', display:'flex', flexDirection:'column', justifyContent:'center', background:'var(--offWhite, #FDF5F5)' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:12 },
  h2: { fontFamily:'var(--font-display)', fontSize:38, lineHeight:1.1, margin:'0 0 16px', letterSpacing:'-.01em', textWrap:'balance' },
  body: { fontSize:16, lineHeight:1.6, color:'var(--grey)', margin:'0 0 28px', maxWidth:'48ch' },
  ctaRow: { display:'flex', gap:12, flexWrap:'wrap' },
  side: { padding:'44px 48px', display:'flex', flexDirection:'column', gap:24, justifyContent:'center' },
  sideItem: { display:'grid', gridTemplateColumns:'auto 1fr', gap:18, alignItems:'start' },
  sideNum: { fontFamily:'var(--font-display)', fontSize:26, color:'var(--brandRed)', lineHeight:1, fontVariantNumeric:'tabular-nums' },
  sideHead: { fontSize:15.5, fontWeight:600, color:'var(--fg-1)', marginBottom:4 },
  sideBody: { fontSize:14, color:'var(--grey)', lineHeight:1.55 },
};
